import { StudentName } from "../../../shared-components/StudentName";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { AnswerStatus } from "./AnswerStatus";
import { useNavigate } from "react-router-dom";
import ExtractionQualityReviewFlag from "./ExtractionQualityReviewFlag";
import FruitifyIcon from "../../../shared-components/FruitifyIcons";

export const StudentAnswerListItem = ({
  answer,
  assignmentId,
  assignmentQuestionId,
}) => {
  const navigate = useNavigate();

  return (
    <Card
      sx={{
        backgroundColor: "background.paper",
        border: 0,
        boxShadow: 1,
        transition: (theme) => theme.transitions.create("box-shadow"),
        "&:hover": {
          boxShadow: 3,
        },
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(220px, 1fr) auto auto auto auto",
            },
            gap: 2,
            alignItems: "center",
          }}
        >
          {/* Student info */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flexBasis: 250,
              flexGrow: 2.5,
              minWidth: 150,
              gridColumn: { xs: "1 / -1", md: "auto" },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar>
                {(answer.student?.identifier || "?").toString().charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  <StudentName student={answer.student} />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Submitted{" "}
                  {formatDistanceToNow(new Date(answer.submittedAt), {
                    addSuffix: true,
                  })}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Viewed / Not Viewed */}
          <Box
            sx={{
              flexBasis: 100,
              flexGrow: 1,
              minWidth: 80,
            }}
          >
            <Chip
              label={answer.viewed ? "Viewed" : "Not Viewed"}
              color={answer.viewed ? "success" : "default"}
            />
          </Box>

          {/* Answer Status */}
          <Box
            sx={{
              flexBasis: 150,
              flexGrow: 1.5,
              minWidth: 120,
              display: "flex",
              justifyContent: { xs: "flex-start", md: "flex-start" },
            }}
          >
            <AnswerStatus answer={answer} />
          </Box>

          {/* Quality Warning */}
          <Box
            sx={{
              flexBasis: 50,
              flexGrow: 0.5,
              minWidth: 40,
              display: "flex",
              justifyContent: { xs: "flex-start", md: "center" },
              alignItems: "center",
            }}
          >
            <ExtractionQualityReviewFlag answer={answer} />
          </Box>

          {/* View button */}
          <Box
            sx={{
              flexBasis: 100,
              flexGrow: 1,
              minWidth: 80,
              display: "flex",
              justifyContent: { xs: "flex-start", md: "flex-end" },
              gridColumn: { xs: "1 / -1", md: "auto" },
            }}
          >
            <Button
              variant="contained"
              size="small"
              onClick={() =>
                navigate(
                  `/assignments/answer/view/${assignmentId}/${answer.student.id}?aq=${assignmentQuestionId}`,
                )
              }
            >
              View
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export const AnswersBox = ({ answers, assignmentId, assignmentQuestionId }) => {
  const [answerStatus, setAnswerStatus] = useState("all");
  const theme = useTheme();

  const totalCount = answers?.length || 0;
  const viewedCount = useMemo(
    () => (answers ? answers.filter((a) => a.viewed).length : 0),
    [answers],
  );
  const notViewedCount = totalCount - viewedCount;

  React.useEffect(() => {
    if (totalCount === 0 && answerStatus !== "all") {
      setAnswerStatus("all");
    }
  }, [totalCount, answerStatus]);

  const filteredAnswers = useMemo(() => {
    if (answerStatus === "all") return answers;
    if (answerStatus === "viewed")
      return answers?.filter((a) => a.viewed) || [];
    if (answerStatus === "not viewed")
      return answers?.filter((a) => !a.viewed) || [];
    return answers;
  }, [answers, answerStatus]);

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: { xs: "visible", md: "hidden" },
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h5">Student Answers</Typography>
          <Chip
            size="small"
            label={totalCount}
            color="secondary"
            variant="outlined"
          />
        </Stack>
        <ToggleButtonGroup
          sx={{ mt: 2, width: { xs: "100%", sm: 400 } }}
          value={answerStatus}
          exclusive
          onChange={(_, newAnswerStatus) => setAnswerStatus(newAnswerStatus)}
          aria-label="Answer Status"
        >
          <ToggleButton sx={{ flex: 1 }} value="all">
            All Answers
          </ToggleButton>
          <ToggleButton
            sx={{ flex: 1 }}
            value="viewed"
            disabled={totalCount === 0}
          >
            Viewed
          </ToggleButton>
          <ToggleButton
            sx={{ flex: 1 }}
            value="not viewed"
            disabled={totalCount === 0}
          >
            Not Viewed
          </ToggleButton>
        </ToggleButtonGroup>
        <Stack
          direction={"column"}
          spacing={2}
          sx={{ mt: 2, flex: 1, minHeight: 0 }}
        >
          {(!filteredAnswers || filteredAnswers.length === 0) && (
            <Card
              sx={{
                backgroundColor: theme.palette.background.default,
                flex: 1,
                display: "flex",
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  flex: 1,
                }}
              >
                <FruitifyIcon name={"Assignment"} fontSize={"large"} />
                {totalCount === 0 && (
                  <>
                    <Typography variant={"h5"}>No answers submitted</Typography>
                    <Typography variant={"subtitle1"}>
                      Please upload an answer
                    </Typography>
                  </>
                )}
                {totalCount > 0 &&
                  answerStatus === "viewed" &&
                  viewedCount === 0 && (
                    <>
                      <Typography variant={"h5"}>
                        No answers have been viewed yet
                      </Typography>
                      <Typography variant={"subtitle1"}>
                        View an answer to see it here
                      </Typography>
                    </>
                  )}
                {totalCount > 0 &&
                  answerStatus === "not viewed" &&
                  notViewedCount === 0 && (
                    <>
                      <Typography variant={"h5"}>All caught up 🎉</Typography>
                      <Typography variant={"subtitle1"}>
                        All submitted answers have been viewed
                      </Typography>
                    </>
                  )}
              </CardContent>
            </Card>
          )}
          {filteredAnswers && filteredAnswers.length > 0 && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                backgroundColor: "background.default",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                p: 1.5,
                scrollbarGutter: "stable",
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: theme.palette.action.disabled,
                  borderRadius: 4,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: theme.palette.background.paper,
                },
              }}
            >
              <Stack direction="column" spacing={1}>
                {filteredAnswers.map((answer, idx) => (
                  <StudentAnswerListItem
                    key={idx}
                    answer={answer}
                    assignmentId={assignmentId}
                    assignmentQuestionId={assignmentQuestionId}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
